import { FC, useState } from "react";
import { AxiosError } from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import $api from "../../shared/api/axios";
import { getGlobalSettings } from "../../shared/services/globalSettings";
import {
  updateOrganizationAiModel,
  updateOrganizationStatus,
  createOrganization
} from "../../shared/services/organizations";
import { MODEL_OPTIONS } from "../../shared/constants/models";
import { getRouteFeatureFlags } from "../../shared/const/router";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";

type TOrganization = {
  id: number;
  name: string;
  defaultAiModelOverride?: string | null;
  status: "active" | "archived";
  users?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  }[];
};

type TFeatureFlagWithOverrides = {
  key: string;
  name: string;
  description: string;
  overrides: { organizationId: number; enabled: boolean }[];
};

const FLAG_COLUMNS = [
  { key: "two-factor-auth", label: "2FA" },
  { key: "enhanced-password-policy", label: "Password Policy" }
] as const;

const roleLabels = [
  { label: "Member", value: "clientUser" },
  { label: "Admin", value: "clientAdmin" },
  { label: "System Admin", value: "systemAdmin" }
] as const;

function FlagCell({ orgId, flagKey, flags }: { orgId: number; flagKey: string; flags: TFeatureFlagWithOverrides[] }) {
  const flag = flags.find((f) => f.key === flagKey);
  const enabled = flag?.overrides.some((o) => o.organizationId === orgId && o.enabled) ?? false;

  return (
    <div className="flex items-center gap-1.5">
      {enabled ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-green-100 text-green-800">
          Enabled
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-gray-100 text-gray-600">
          Disabled
        </span>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[10px] font-medium cursor-default">i</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            Manage via{" "}
            <Link to={`${getRouteFeatureFlags()}?tab=by-org&orgId=${orgId}&flagKey=${flagKey}`} className="text-blue-400 underline">
              Feature Flags
            </Link>
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export const OrganizationsPage: FC = () => {
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");

  const {
    data: organizationsData,
    isLoading,
    error
  } = useQuery<TOrganization[]>({
    queryKey: ["organizations-with-users"],
    queryFn: async () => {
      const response = await $api.get("/organizations/with-users");
      return response?.data?.data || [];
    }
  });

  const { data: allFlags = [] } = useQuery<TFeatureFlagWithOverrides[]>({
    queryKey: ["admin-feature-flags"],
    queryFn: async () => {
      const response = await $api.get("/admin/feature-flags");
      return response?.data || [];
    }
  });

  const { data: globalSettings } = useQuery({
    queryKey: ["globalSettings"],
    queryFn: getGlobalSettings
  });

  const updateMutation = useMutation({
    mutationFn: ({ orgId, model }: { orgId: number; model: string | null }) =>
      updateOrganizationAiModel(orgId, { defaultAiModelOverride: model }),
    onSuccess: () => {
      toast.success("Organization AI model updated successfully");
      queryClient.invalidateQueries({ queryKey: ["organizations-with-users"] });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = error.response?.data?.message || "Failed to update organization AI model";
      toast.error(errorMessage);
    }
  });

  const statusMutation = useMutation({
    mutationFn: ({ orgId, status }: { orgId: number; status: "active" | "archived" }) =>
      updateOrganizationStatus(orgId, { status }),
    onMutate: async ({ orgId, status }) => {
      await queryClient.cancelQueries({ queryKey: ["organizations-with-users"] });
      const previousOrganizations = queryClient.getQueryData<TOrganization[]>(["organizations-with-users"]);
      queryClient.setQueryData<TOrganization[]>(["organizations-with-users"], (old) => {
        if (!old) return old;
        return old.map((org) => (org.id === orgId ? { ...org, status } : org));
      });
      return { previousOrganizations };
    },
    onSuccess: (_, variables) => {
      toast.success(`Organization ${variables.status === "archived" ? "archived" : "unarchived"} successfully`);
    },
    onError: (error: AxiosError<{ message: string }>, _variables, context) => {
      if (context?.previousOrganizations) {
        queryClient.setQueryData(["organizations-with-users"], context.previousOrganizations);
      }
      const errorMessage = error.response?.data?.message || "Failed to update organization status";
      toast.error(errorMessage);
    }
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => createOrganization({ name }),
    onSuccess: () => {
      toast.success("Organization created successfully");
      queryClient.invalidateQueries({ queryKey: ["organizations-with-users"] });
      setCreateDialogOpen(false);
      setNewOrgName("");
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = error.response?.data?.message || "Failed to create organization";
      toast.error(errorMessage);
    }
  });

  const handleCreateOrganization = () => {
    if (!newOrgName.trim()) return;
    createMutation.mutate(newOrgName.trim());
  };

  const handleModelSelect = (orgId: number, model: string | null) => {
    updateMutation.mutate({ orgId, model });
  };

  const handleStatusSelect = (orgId: number, status: "active" | "archived") => {
    statusMutation.mutate({ orgId, status });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading organizations...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-red-600">Error loading organizations</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8" data-testid="organizations-page">
      <div className="flex justify-between items-center mb-6 pr-6">
        <FigmaTypography component="h1" variant="heading-1" data-testid="organizations-heading">Organizations</FigmaTypography>
        <AppButton appearance="contained" onClick={() => setCreateDialogOpen(true)} data-testid="create-organization-button">
          Create organization
        </AppButton>
      </div>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="org-name">Organization name</Label>
            <Input
              id="org-name"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Enter organization name"
              onKeyDown={(e) => isEnterSubmit(e, { allowShift: true }) && handleCreateOrganization()}
              data-testid="create-organization-name-input"
            />
          </div>
          <DialogFooter>
            <AppButton appearance="outlined" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </AppButton>
            <AppButton
              appearance="contained"
              onClick={handleCreateOrganization}
              disabled={!newOrgName.trim() || createMutation.isPending}
              data-testid="create-organization-submit"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </AppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-card shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="organizations-table">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Organization Name</th>
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Organization ID</th>
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Status</th>
                {FLAG_COLUMNS.map((col) => (
                  <th key={col.key} className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">{col.label}</th>
                ))}
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">AI Model</th>
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Users & Roles</th>
                <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizationsData?.map((org) => {
                const isArchived = org.status === "archived";
                return (
                  <tr key={org.id} className={`border-b hover:bg-gray-50 ${isArchived ? "opacity-60" : ""}`} data-testid={`org-row-${org.id}`}>
                    <td className="px-6 py-4 text-[14px] font-medium text-gray-900">{org.name}</td>
                    <td className="px-6 py-4 text-[14px] text-gray-600">{org.id}</td>
                    <td className="px-6 py-4 text-[14px] text-gray-600">
                      {isArchived ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-orange-100 text-orange-800">
                          Archived
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </td>
                    {FLAG_COLUMNS.map((col) => (
                      <td key={col.key} className="px-6 py-4 text-[14px] text-gray-600">
                        <FlagCell orgId={org.id} flagKey={col.key} flags={allFlags} />
                      </td>
                    ))}
                    <td className="px-6 py-4 text-[14px] text-gray-600">
                      <div className="flex items-center gap-2">
                        {org.defaultAiModelOverride ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-green-100 text-green-800">
                            {org.defaultAiModelOverride}
                          </span>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                Default
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Using global default: {globalSettings?.defaultOpenAiModel || "Loading..."}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <AppButton
                              size="sm"
                              appearance="text"
                              className="h-6 px-2"
                              disabled={updateMutation.isPending}
                              data-testid={`org-model-change-${org.id}`}
                            >
                              Change
                            </AppButton>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                            <DropdownMenuItem
                              onClick={() => handleModelSelect(org.id, null)}
                              className="cursor-pointer"
                            >
                              Use Global Default
                            </DropdownMenuItem>
                            {MODEL_OPTIONS.map((model) => (
                              <DropdownMenuItem
                                key={model.value}
                                onClick={() => handleModelSelect(org.id, model.value)}
                                className="cursor-pointer"
                              >
                                {model.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[14px] text-gray-600">
                      {org.users && org.users.length > 0 ? (
                        <div className="space-y-1">
                          {org.users.map((user) => (
                            <div key={user.id} className="flex items-center space-x-2">
                              <span className="font-medium">
                                {user.firstName} {user.lastName}
                              </span>
                              <span className="text-gray-500">({user.email})</span>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium bg-blue-100 text-blue-800">
                                {roleLabels.find((role) => role.value === user.role)?.label || user.role}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">No users</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[14px] text-gray-600">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <AppButton
                            size="sm"
                            appearance="outlined"
                            disabled={statusMutation.isPending}
                            className="min-w-[120px]"
                            data-testid={`org-status-trigger-${org.id}`}
                          >
                            {org.status === "archived" ? "Archived" : "Active"}
                          </AppButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem
                            onClick={() => handleStatusSelect(org.id, "active")}
                            className="cursor-pointer"
                            disabled={org.status === "active"}
                          >
                            Active
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleStatusSelect(org.id, "archived")}
                            className="cursor-pointer"
                            disabled={org.status === "archived"}
                          >
                            Archived
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {organizationsData?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No organizations found</div>
        </div>
      )}
    </div>
  );
};
